// import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
// // Adjust this import to wherever your RootState lives (same as your other slices).
// import type { RootState } from "@/store";

// export interface InvoiceSessionState {
//   lightning: { invoice: string; invoiceId: string };
//   // invoice = the ADDRESS (bcrt1q...); invoiceId = the order id.
//   onchain: { invoice: string; invoiceId: string; btcAmount: number };
//   // Absolute deadline (epoch milliseconds — a fixed point in time, NOT a
//   // "seconds left" counter). Persisting this single number is what makes the
//   // timer refresh-proof: remaining = expiresAt - Date.now(), so a refresh at
//   // minute 7 resumes at exactly 8:00 left.
//   expiresAt: number | null;
// }

// const initialState: InvoiceSessionState = {
//   lightning: { invoice: "", invoiceId: "" },
//   onchain: { invoice: "", invoiceId: "", btcAmount: 0 },
//   expiresAt: null,
// };

// const invoiceSessionSlice = createSlice({
//   name: "invoiceSession",
//   initialState,
//   reducers: {
//     // Written ONCE per mint (in createInvoiceMutation.onSuccess). Nothing else
//     // ever touches expiresAt — that is the invariant that stops timer resets.
//     setInvoiceSession: (_state, action: PayloadAction<InvoiceSessionState>) =>
//       action.payload,
//     resetInvoiceSession: () => initialState,
//   },
// });

// export const { setInvoiceSession, resetInvoiceSession } =
//   invoiceSessionSlice.actions;

// export const getInvoiceSession = (state: RootState) => state.invoiceSession;

// export default invoiceSessionSlice.reducer;

// ============================================================================
// DESTINATION:  src/store/slices/invoiceSessionSlice/invoiceSessionSlice.ts
// ACTION:       REPLACE your existing file
// ============================================================================

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/store";

/**
 * ============================================================================
 * WHAT CHANGED FROM YOURS
 * ============================================================================
 *   + sessionVersion on the state           (THE STAMP — the whole expiry feature)
 *   + selectIsInvoiceStale selector
 * The lightning/onchain/expiresAt shape and your one-write-per-mint invariant are
 * unchanged.
 */

export interface InvoiceSessionState {
  lightning: { invoice: string; invoiceId: string };
  // invoice = the ADDRESS (bcrt1q...); invoiceId = the order id.
  onchain: { invoice: string; invoiceId: string; btcAmount: number };
  // Absolute deadline (epoch ms). Persisting this single number is what makes the
  // timer refresh-proof: remaining = expiresAt - Date.now().
  expiresAt: number | null;

  /**
   * ▼ THE STAMP. The sessionVersion this invoice was minted under.
   *
   * The invoice is valid only while `invoice.sessionVersion === auth.sessionVersion`.
   * When they diverge — user clicked a promo link in another tab, an admin edited
   * the discount, the referral changed — the price on this invoice is for a
   * session that no longer exists. selectIsInvoiceStale is how the page notices.
   *
   * null = an invoice minted before this field existed, or no invoice yet. Treated
   * as "cannot judge" — never as stale.
   */
  sessionVersion: number | null;
}

const initialState: InvoiceSessionState = {
  lightning: { invoice: "", invoiceId: "" },
  onchain: { invoice: "", invoiceId: "", btcAmount: 0 },
  expiresAt: null,
  sessionVersion: null,
};

const invoiceSessionSlice = createSlice({
  name: "invoiceSession",
  initialState,
  reducers: {
    // Written ONCE per mint. Nothing else ever touches expiresAt or
    // sessionVersion — that is the invariant that stops timer resets AND stops
    // the stamp drifting from the invoice it belongs to.
    setInvoiceSession: (_state, action: PayloadAction<InvoiceSessionState>) =>
      action.payload,
    resetInvoiceSession: () => initialState,
  },
});

export const { setInvoiceSession, resetInvoiceSession } =
  invoiceSessionSlice.actions;

export const getInvoiceSession = (state: RootState) => state.invoiceSession;

/**
 * ============================================================================
 * selectIsInvoiceStale — is the invoice on screen for a dead session?
 * ============================================================================
 * EVERY guard returns FALSE. That is deliberate and it is the most important
 * property of this selector:
 *
 *   "I do not know yet" must NEVER resolve to "stale".
 *
 * If it did, the page would drop and re-mint the invoice on every render where
 * the version has not loaded — including the first paint after a refresh, before
 * the /session/status poll has answered. The user would watch their QR code flash
 * and regenerate for no reason, and an in-flight payment could be aimed at an
 * address that just got replaced.
 *
 * Stale is returned ONLY when we positively know both numbers AND they differ.
 */
export const selectIsInvoiceStale = (state: RootState): boolean => {
  const invoiceVersion = state.invoiceSession.sessionVersion;
  const liveVersion = state.auth.sessionVersion;

  // No invoice minted -> nothing to be stale.
  if (state.invoiceSession.expiresAt === null) return false;

  // Invoice predates the stamp -> cannot judge, do not disrupt.
  if (invoiceVersion === null) return false;

  // Server has not told us the current version yet -> unknown, not stale.
  if (liveVersion === null) return false;

  // Both known. Stale iff they actually differ.
  return invoiceVersion !== liveVersion;
};

export default invoiceSessionSlice.reducer;
